import clsx from 'clsx';
import Image from 'next/image';
import React, { useRef, useState } from 'react';

import avatar1 from '../../../../assets/TestimonialSection/avatar1.png';
import avatar2 from '../../../../assets/TestimonialSection/avatar2.png';
import avatar3 from '../../../../assets/TestimonialSection/avatar3.png';
import headerBadge from '../../../../assets/TestimonialSection/Container.png';
import nehaAvatar from '../../../../assets/TestimonialSection/neha.png';

interface Testimonial {
  id: number;
  content: string;
  name: string;
  position: string;
  avatar: any;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    content:
      'Joining ZebraLearn made my daily learning more structured. The bite sized books help me quickly grasp concepts and apply them while mentoring early stage founders.',
    name: 'Aman Bhatia',
    position: 'Startup Mentor',
    avatar: avatar1,
  },
  {
    id: 2,
    content:
      'ZebraLearn has become my go to platform for practical reading. I can explore multiple topics without committing to a single book and that flexibility really matters.',
    name: 'Ritika Malhotra',
    position: 'Business Manager',
    avatar: avatar3,
  },
  {
    id: 3,
    content:
      'What I like most about ZebraLearn is how relevant the content feels. It saves me time and still gives me insights I can use directly in my work.',
    name: 'Kunal Mehra',
    position: 'Growth Consultant',
    avatar: avatar2,
  },
  {
    id: 4,
    content:
      'With ZebraLearn, I am able to keep learning consistently despite a busy schedule. The curated content makes learning feel less overwhelming.',
    name: 'CA Neha Kulkarni',
    position: 'Founder & CEOs',
    avatar: nehaAvatar,
  },
  {
    id: 5,
    content:
      'ZebraLearn fits perfectly into my learning routine. I use it to sharpen my understanding before client discussions and workshops.',
    name: 'Siddharth Jain',
    position: 'Business Coach',
    avatar: avatar1,
  },
];

const TestimonialSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      const maxScroll = scrollWidth - clientWidth;
      if (maxScroll <= 0) {
        setActiveIndex(0);
        return;
      }
      const scrollRatio = scrollLeft / maxScroll;
      const index = Math.round(scrollRatio * (testimonials.length - 1));
      setActiveIndex(Math.min(Math.max(index, 0), testimonials.length - 1));
    }
  };

  const scrollToIndex = (index: number) => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const children = container.children;
      if (children[index]) {
        (children[index] as HTMLElement).scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });
        setActiveIndex(index);
      }
    }
  };

  return (
    <div className='py-12 md:py-16 w-full flex flex-col items-center overflow-hidden'>
      {/* Top Graphic Badge */}
      <div className='flex justify-center mb-2'>
        <Image
          src={headerBadge}
          alt='ZebraLearn Users'
          width={1000}
          height={1000}
          className='w-auto h-[100px] md:h-[200px] object-contain'
          priority
        />
      </div>

      {/* Heading */}
      <h2 className='text-xl md:text-3xl lg:text-[34px] font-semibold text-white text-center mb-[25px] md:mb-[25px] tracking-tight'>
        Hear from Our Users
      </h2>

      {/* Testimonials Cards Slider */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className='w-full flex gap-4 md:gap-5 overflow-x-auto no-scrollbar scroll-smooth snap-x snap-mandatory py-2 pl-6 sm:pl-10 md:pl-16 lg:pl-20 xl:pl-28 pr-6 md:pr-10 scroll-pl-6 sm:scroll-pl-10 md:scroll-pl-16 lg:scroll-pl-20 xl:scroll-pl-10'
      >
        {testimonials.map((item) => (
          <div
            key={item.id}
            className={clsx(
              'w-[290px] sm:w-[320px] md:w-[320px] lg:w-[320px] xl:w-[379px] shrink-0 snap-start',
              'bg-[#141414] border border-white/5 rounded-[20px] p-6 md:p-7 flex flex-col justify-between',
              'shadow-lg transition-transform duration-300 hover:border-[#383838]',
            )}
          >
            <p className='text-[#D4D4D8] text-[13.5px] md:text-[18px] leading-[-0.5%] mb-6 font-medium'>
              {item.content}
            </p>

            <div className='flex items-center gap-3 pt-6 border-t border-white/5'>
              <div className='md:w-[55px] w-[30px] md:h-[55px] h-[30px] rounded-lg overflow-hidden shrink-0 bg-[#222]'>
                <Image
                  src={item.avatar}
                  alt={item.name}
                  width={100}
                  height={100}
                  className='w-full h-full object-cover'
                />
              </div>
              <div className='flex flex-col text-left'>
                <p className='text-white font-medium md:text-lg text-sm leading-snug'>
                  {item.name}
                </p>
                <p className='text-[#8E8E93] md:text-[15px] text-xs font-medium'>
                  {item.position}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Indicator Dots */}
      <div className='flex items-center justify-center gap-1.5 mt-8'>
        {testimonials.map((_, idx) => (
          <button
            key={idx}
            onClick={() => scrollToIndex(idx)}
            aria-label={`Go to slide ${idx + 1}`}
            className={clsx(
              'h-1 rounded-full transition-all duration-300 cursor-pointer',
              activeIndex === idx ? 'w-6 bg-white' : 'w-3 bg-[#3A3A3C] hover:bg-[#555]',
            )}
          />
        ))}
      </div>
    </div>
  );
};

export default TestimonialSection;
